/**
 * Pack-card grid for the starter zero state (S21) — presentational only.
 * `StarterDashboards.tsx` owns the connection pick, preflight run and
 * dashboard creation; this just renders the four choices and their
 * loading/disabled affordances.
 */
import { Activity, HardDrive, HeartPulse, LayoutGrid } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { cn } from '@/lib/utils';
import { STARTER_PACK_LABELS, type StarterPack } from '../services/starterPreflight';

const PACK_CARDS: Array<{ pack: StarterPack; icon: typeof HeartPulse; blurb: string }> = [
  { pack: 'health', icon: HeartPulse, blurb: 'Waits, cache ratios and index health — the day-to-day pulse.' },
  { pack: 'storage', icon: HardDrive, blurb: 'Sizes, growth and backups — what is filling up.' },
  { pack: 'activity', icon: Activity, blurb: 'Who and what is running right now.' },
];

export function StarterPackPicker({
  disabledReason,
  running,
  onPick,
  onBlank,
}: {
  /** Non-null disables the three connection-scoped packs, with the WHY as a tooltip. */
  disabledReason: string | null;
  running: StarterPack | 'blank' | null;
  onPick: (pack: StarterPack) => void;
  onBlank: () => void;
}) {
  const busyReason = 'A pack is already loading — please wait';
  const disabled = Boolean(disabledReason);

  return (
    <>
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
        {PACK_CARDS.map(({ pack, icon: Icon, blurb }) => (
          // Disabled `<button>`s don't reliably surface their own `title`
          // tooltip on hover (the same reason TreeNodeContextMenu.tsx keeps
          // disabled items pointer-events-enabled) — the WHY reason lives on
          // this wrapping `<span>` instead, same convention as the
          // Switcher's disabled "New" button and the explorer context
          // menu's disabled "Duplicate" item.
          <span key={pack} title={disabled ? (disabledReason ?? undefined) : undefined}>
            <button
              type="button"
              onClick={() => onPick(pack)}
              disabled={disabled}
              className={cn(
                'flex h-full w-full flex-col items-center gap-1.5 rounded-lg border px-3 py-4 text-center transition-colors',
                disabled
                  ? 'cursor-not-allowed border-border bg-card opacity-60'
                  : 'border-border bg-card hover:border-primary/40 hover:bg-accent hover:shadow-sm',
              )}
              data-testid={`starter-pack-${pack}`}
            >
              {running === pack ? (
                <RandomDotmSpinner size={20} />
              ) : (
                <Icon
                  className={cn('h-5 w-5', disabled ? 'text-muted-foreground' : 'text-foreground')}
                  aria-hidden
                />
              )}
              <span className="text-sm font-medium text-foreground">{STARTER_PACK_LABELS[pack]}</span>
              <span className="text-[11px] text-muted-foreground">{blurb}</span>
            </button>
          </span>
        ))}
      </div>

      <button
        type="button"
        onClick={onBlank}
        disabled={running !== null}
        title={running !== null ? busyReason : undefined}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="starter-pack-blank"
      >
        {running === 'blank' ? (
          <RandomDotmSpinner size={14} />
        ) : (
          <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
        )}
        Start blank instead
      </button>
    </>
  );
}
